import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'

interface RemoveCitationDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Called with the writer's "don't ask again this session" choice. */
  onConfirm: (suppressFutureDialogs: boolean) => void
}

/**
 * Confirmation modal for converting "remove last source" → "remove citation".
 * Per D4: this is a doc-mutating action — the citation node is removed from
 * prose. Sentence text stays. Claim row stays in DB. ⌘Z restores in-session;
 * the session-scoped restore stack (D11) covers the cross-action case.
 *
 * The "don't ask again this session" checkbox is React-state-only; it resets
 * on reload. Pharma writers hit this kind of dialog dozens of times a day —
 * skipping a confirmed-once dialog is the difference between guardrail and tax.
 */
export function RemoveCitationDialog({ open, onOpenChange, onConfirm }: RemoveCitationDialogProps) {
  const [suppressFutureDialogs, setSuppressFutureDialogs] = useState(false)

  function handleConfirm() {
    onConfirm(suppressFutureDialogs)
    setSuppressFutureDialogs(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Remove citation?</DialogTitle>
          <DialogDescription>
            The citation marker will be removed from the text. The sentence stays unchanged.
            Use ⌘Z to undo, or run a new audit on this paragraph to re-evaluate.
          </DialogDescription>
        </DialogHeader>
        <label className="flex items-center gap-2 text-sm text-muted-foreground select-none">
          <input
            type="checkbox"
            checked={suppressFutureDialogs}
            onChange={(e) => setSuppressFutureDialogs(e.target.checked)}
            className="h-4 w-4 accent-current"
          />
          Don't ask again this session
        </label>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleConfirm}>Remove citation</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
